import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, AlertTriangle, Clock, Moon, Send, Activity, ChevronRight } from 'lucide-react';

function isWorkdayParis() {
  const offsetH = (() => {
    const m = new Date().getUTCMonth() + 1;
    return m >= 3 && m <= 10 ? 2 : 1;
  })();
  const paris = new Date(Date.now() + offsetH * 3600 * 1000);
  const dow = paris.getUTCDay(); // 0 = Sun, 6 = Sat
  return dow >= 1 && dow <= 5;
}

function parisHour() {
  const offsetH = (() => {
    const m = new Date().getUTCMonth() + 1;
    return m >= 3 && m <= 10 ? 2 : 1;
  })();
  const paris = new Date(Date.now() + offsetH * 3600 * 1000);
  return paris.getUTCHours();
}

function dailyState(item) {
  // Returns { tone, icon, title, sub }
  if (!isWorkdayParis()) {
    return { tone: 'neutral', icon: Moon, title: 'Jour non ouvré', sub: 'Pas d\'envoi prévu aujourd\'hui' };
  }
  if (!item.enabled) {
    return { tone: 'muted', icon: AlertTriangle, title: 'Désactivé', sub: 'Activez dans le Centre de notifications' };
  }
  const sent = item.today_sent || 0;
  const total = item.today_total || 0;
  if (sent > 0 && total > 0) {
    const time = item.last_sent ? new Date(item.last_sent).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—';
    return { tone: 'ok', icon: CheckCircle2, title: `Envoyés à ${time}`, sub: `${sent}/${total} commerciaux` };
  }
  const h = parisHour();
  if (h < 8) {
    return { tone: 'pending', icon: Clock, title: 'Pas encore l\'heure', sub: 'Envoi prévu à 08:00' };
  }
  if (h < 12) {
    return { tone: 'pending', icon: Clock, title: 'Rattrapage en cours', sub: `Le scheduler retentera avant 12:00${h >= 11 ? ' · alerte admin émise' : ''}` };
  }
  return { tone: 'warn', icon: AlertTriangle, title: 'Non envoyés', sub: 'Cliquer pour relancer manuellement' };
}

function genericState(item, label) {
  if (!item.enabled) return { tone: 'muted', icon: AlertTriangle, title: 'Désactivé', sub: '' };
  if (item.last_sent) {
    const d = new Date(item.last_sent);
    const dayLabel = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
    return { tone: 'ok', icon: CheckCircle2, title: `Dernier : ${dayLabel}`, sub: label };
  }
  return { tone: 'pending', icon: Clock, title: 'Aucun envoi récent', sub: label };
}

const TONE_COLORS = {
  ok: { bg: '#10B98112', fg: '#059669', border: '#10B98130' },
  pending: { bg: '#0055FF12', fg: '#0055FF', border: '#0055FF30' },
  warn: { bg: '#F9731612', fg: '#EA580C', border: '#F9731640' },
  muted: { bg: '#71717A12', fg: '#71717A', border: '#71717A30' },
  neutral: { bg: '#A1A1AA12', fg: '#52525B', border: '#A1A1AA30' },
};

function Tile({ state, label }) {
  const Icon = state.icon;
  const c = TONE_COLORS[state.tone] || TONE_COLORS.neutral;
  return (
    <div className="rounded-xl border bg-white p-3.5 transition-all hover:shadow-sm" style={{ borderColor: c.border }}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: c.bg, color: c.fg }}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-[11px] uppercase tracking-wider text-[#71717A] font-semibold">{label}</span>
      </div>
      <p className="text-sm font-semibold leading-tight" style={{ color: c.fg }}>{state.title}</p>
      {state.sub && <p className="text-xs text-[#71717A] mt-0.5 leading-snug">{state.sub}</p>}
    </div>
  );
}

export default function AutomationsStatusCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get('/notifications/center');
      setItems(data.items || []);
    } catch (_e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading || items.length === 0) return null;

  const byKey = Object.fromEntries(items.map(i => [i.key, i]));
  const daily = byKey.daily_opportunities;
  const weekly = byKey.weekly_report;
  const enr = byKey.weekly_enrichment;
  const rdv = byKey.rdv;
  if (!daily) return null;

  const dState = dailyState(daily);
  const triggerDaily = async () => {
    if (triggering) return;
    setTriggering(true);
    try {
      await api.post('/opportunities/generate-now');
      await load();
    } catch (_e) { /* ignore */ }
    setTriggering(false);
  };

  const showRelaunch = dState.tone === 'warn';

  return (
    <Card className="border border-[#E5E7EB] shadow-sm" data-testid="automations-status-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#0055FF]" />
            <h3 className="text-sm font-semibold tracking-tight">Statut des automatisations</h3>
          </div>
          <button
            onClick={() => navigate('/settings')}
            className="text-xs text-[#71717A] hover:text-[#18181B] flex items-center gap-0.5 transition-colors"
            data-testid="automations-status-open-settings"
          >
            Centre de notifications <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative" data-testid="automations-daily-tile">
            <Tile state={dState} label="Opportunités du jour" />
            {showRelaunch && (
              <button
                onClick={triggerDaily}
                disabled={triggering}
                className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md bg-[#F97316] text-white hover:bg-[#EA580C] transition-colors disabled:opacity-50"
                data-testid="automations-trigger-daily"
              >
                <Send className="w-3 h-3" />
                {triggering ? 'Envoi…' : 'Relancer'}
              </button>
            )}
          </div>
          {weekly && <Tile state={genericState(weekly, 'Bilan vendredi')} label="Bilan hebdo" />}
          {enr && <Tile state={genericState(enr, 'Synthèse lundi')} label="Enrichissement" />}
          {rdv && <Tile state={genericState(rdv, 'Temps réel')} label="Notification RDV" />}
        </div>
      </CardContent>
    </Card>
  );
}
